(ns minorodata.components.log.controls
  (:require ["date-fns" :refer [format isValid]]
            [goog.functions :refer [debounce]]
            [lucide-react :refer [ChevronDown ChevronLeft ChevronRight FilterX
                                  RefreshCw Search ChevronsLeft]]
            [minorodata.app.handlers :refer [dispatch dispatch-sync]]
            [minorodata.app.posthog-events :as posthog-events]
            [minorodata.app.utils :as app.utils]
            [minorodata.logs.model :refer [->logs-view]]
            [minorodata.utils :as utils]
            [mold-forms.core :as form-component]
            [mold-forms.model :refer [->form-model]]
            [re-frame.core :as re-frame]
            [re-posh.core :as re-posh]
            [ui.button :as button]
            [ui.datepicker :as datepicker]
            [ui.fields :as fields]
            [ui.popover :as popover]
            [ui.utils :refer [clsx]]))

(def node-ref (atom nil))

;; hack, used to rerender uncontrolled :search-term input
(def term-last-updated-time (atom ""))

(defn submit-logs-filter-form [form-state]
  (let [form-data (form-component/model->fields form-state)
        filters   (app.utils/assoc-some
                   {}
                   :logs-view/type (:log-type form-data)
                   :logs-view/source (:log-source form-data)
                   :logs-view/term (:search-term form-data))]
    (when-not (empty? filters)
      (dispatch [:logs-view/update filters])
      (dispatch [:logs.api/fetch]))))

(defn remove-namespace
  [key]
  (some-> key name keyword))

(defn apply-query-params [param]
  (dispatch-sync [:logs-view/update (->logs-view param) {:replace true}]))

(defn unns [obj]
  (into {} (map (fn [[k v]] [(remove-namespace k) v]) obj)))

(defn logs-view->query [logs-view]
  (unns (dissoc logs-view :db/id :logs-view/singleton-id)))

(def ^:private query-key->filter-type
  {:date-from "date" :date-to "date" :type "type" :source "source" :term "search"})

(defn- changed-filter-types [query new-query]
  (->> query-key->filter-type
       (keep (fn [[k filter-type]]
               (when (not= (not-empty (str (get query k))) (not-empty (str (get new-query k))))
                 filter-type)))
       distinct
       sort
       vec))

(defn submit-filters []
  (let [{:app-route/keys [name params query]} @(re-posh/subscribe [:app-route/pull])
        logs-view @(re-posh/subscribe [:logs-view/pull])
        new-query (->> (merge query (logs-view->query logs-view))
                       (filter (comp second identity))
                       (into {}))
        filter-types (changed-filter-types query new-query)]
    (when (and (= :log name) (seq filter-types))
      (dispatch [:posthog/capture {:event (:logs/filtered posthog-events/events)
                                   :props {:filter-types filter-types}}]))
    (dispatch [:app.events/navigate name params new-query])

    (some-> @node-ref (.closest ".overflow-y-auto") (.scrollTo 0 0))))

(defn filter-action [filter-name]
  (fn [value]
    (apply-query-params {(remove-namespace filter-name) value
                         :page                          0})))

(def term-action
  (debounce (filter-action :logs-view/term) 500))

(defn apply-logs-pagination [new-page]
  (apply-query-params {:page new-page})
  (submit-filters))

(defn apply-logs-search [{:logs-view/keys [type term source]} & [auto-commit?]]
  (apply-query-params (into {} (filter val {:source source :term term :type type :page 0})))
  (when auto-commit?
    (submit-filters)))

(defn clear-filters []
  (dispatch [:logs-view/delete])
  (dispatch [:logs-view/add (->logs-view {:page 0})])
  (dispatch [:app.events/clear-query]))


(defn refresh-logs []
  (dispatch [:logs.api/fetch])
  (dispatch [:logs.api/fetch-pagination]))


(def type-options
  [{:value nil :text "All"}
   {:value "SUCCESS" :text "Success"}
   {:value "FAILURE" :text "Failure"}
   {:value "INFO" :text "Info"}])

(defn filter-by-type
  [{{:keys [value action]} :field}]
  [fields/select {:label     "Filter by type"
                  :native    false
                  :options   type-options
                  :value     (or value "")
                  :on-change action
                  :placeholder "All"
                  :field-props {:class "w-32"}}])

(defn ->menu-option
  [{:keys [name id]}]
  {:value id :text name})

(defn filter-by-date [{date-from-field :date-from
                       date-to-field   :date-to}]
  (let [date-from   (some->> (:value date-from-field) (new js/Date))
        date-to     (some->> (:value date-to-field) (new js/Date))
        query-dates (if (and (isValid date-from) (isValid date-to))
                      [date-from date-to]
                      [])]
    [datepicker/datetime-picker {:label     "Filter by date"
                                 :value     query-dates
                                 :on-change (fn [[new-date-from new-date-to]]
                                              ((:action date-from-field) (or (some-> new-date-from (format "yyyy-MM-dd HH:mm")) ""))
                                              ((:action date-to-field) (or (some-> new-date-to (format "yyyy-MM-dd HH:mm")) "")))}]))

(defn filter-by-source [{{:keys [value action]} :field}]
  (let [unsorted       @(re-frame/subscribe [:logs.subs/user-friendly-log-sources])
        sources        (sort-by :name unsorted)

        system-sources (utils/find-all-by-field sources :type :system)
        used-sources   (utils/find-all-by-field sources :type :used-connector)
        unused-sources (utils/find-all-by-field sources :type :unused-connector)
        map-options    (partial mapv ->menu-option)

        options [{:value nil :text "All"}
                 {:type :group :name "System events" :options (map-options system-sources)}
                 {:type :group :name "Active extracts" :options (map-options used-sources)}
                 {:type :group :name "Inactive extracts" :options (map-options unused-sources)}]]
    [fields/select {:label       "Filter by source"
                    :native      false
                    :value       value
                    :on-change   action
                    :options     options
                    :placeholder "All"
                    :field-props {:class "2xl:w-52"}}]))

(defn filter-by-term [{{:keys [value action]} :field}]
  [fields/input {:label        "Search"
                 :icon          Search
                 :value         value
                 :on-change     action
                 :class         "min-w-[200px]"}])

(defn refresh-button []
  [button/button {:variant  :empty
                  :icon     RefreshCw
                  :on-click refresh-logs}])

(defn pagination []
  (let [{:log-pagination/keys [page has-more items-per-page]} @(re-posh/subscribe [:log-pagination/pull])
        items-from (+ 1 (* (dec page) items-per-page))
        items-to   (* page items-per-page)]
    [:div {:class   "flex gap-2 items-center"
           :data-cy "logs-pagination"}
     [button/button {:variant  :secondary
                     :size     :icon-md
                     :icon     ChevronsLeft
                     :icon-props {:class (when (<= page 1) "opacity-25")}
                     :disabled (<= page 1)
                     :on-click #(apply-logs-pagination 0)
                     :title "Jump to first page"}]
     [button/button {:variant  :secondary
                     :size     :icon-md
                     :icon     ChevronLeft
                     :icon-props {:class (when (<= page 1) "opacity-25")}
                     :disabled (<= page 1)
                     :on-click #(apply-logs-pagination (- page 2))
                     :title "Previous page"}]
     [:span {:class "text-base"} items-from " - " items-to]
     [button/button {:variant  :secondary
                     :size     :icon-md
                     :icon     ChevronRight
                     :icon-props {:class (when (not has-more) "opacity-25")}
                     :disabled (not has-more)
                     :on-click #(apply-logs-pagination page)
                     :title "Next page"}]]))

(defn clear-filters-button []
  [button/button {:variant  :empty
                  :class    "self-end"
                  :icon     FilterX
                  :on-click clear-filters}
   "Clear"])

(defn confirm-filters-button [{:keys [on-submit]}]
  [button/button {:variant  :primary
                  :on-click on-submit
                  :class    "self-end"}
   "Filter"])

(defn filter-components [{:keys [class]}]
  (let [type      @(re-posh/subscribe [:logs-view/prop :logs-view/type])
        term      @(re-posh/subscribe [:logs-view/prop :logs-view/term])
        source    @(re-posh/subscribe [:logs-view/prop :logs-view/source])
        date-from @(re-posh/subscribe [:logs-view/prop :logs-view/date-from])
        date-to   @(re-posh/subscribe [:logs-view/prop :logs-view/date-to])]
    [form-component/form
     (->form-model
      {:id        :logs-filter-form
       :on-submit submit-filters
       :fields    [{:name          :date-from
                    :value         (or date-from "")
                    :post-action   (filter-action :logs-view/date-from)
                    :process       identity}
                   {:name          :date-to
                    :value         (or date-to "")
                    :post-action   (filter-action :logs-view/date-to)
                    :process       identity}
                   {:name          :type
                    :value         (or type "")
                    :post-action   (filter-action :logs-view/type)
                    :process       #(or % "")}
                   {:name          :source
                    :value         (or source "")
                    :post-action   (filter-action :logs-view/source)
                    :process       #(or % "")}
                   {:name          :term
                    :value         (or term "")
                    :post-action   (filter-action :logs-view/term)}]})
     (fn [fields _ on-submit]
       [:div {:class (clsx "flex justify-start gap-2 *:w-auto min-w-0"
                           class)
              :ref   #(reset! node-ref %)}
        [filter-by-date {:date-from (get fields :date-from)
                         :date-to   (get fields :date-to)}]
        [filter-by-type   {:field (get fields :type)}]
        [filter-by-source {:field (get fields :source)}]
        [filter-by-term {:field (get fields :term)}]
        [:div {:class "flex justify-end gap-2"}
         [clear-filters-button]
         [confirm-filters-button {:on-submit on-submit}]]])]))

(defn filter-dropdown
  []
  [popover/popover {:trigger-as-child false
                    :content          [filter-components {:class "flex-col gap-4"}]}
   [button/button {:variant    :empty
                   :icon-right ChevronDown}
    "Filters"]])

(defn controls []
  [:div {:class "flex justify-between items-end gap-4"}
   [:div {:class "block 2xl:hidden"} [filter-dropdown]]
   [:div {:class "hidden 2xl:block min-w-0 flex-1"}
    [filter-components]]
   [:div {:class "flex gap-2 shrink-0"}
    [refresh-button]
    [pagination]]])
